/**
 * Sync Status Widget Component
 * Displays the synchronization status of AI Planner entities
 * Shows last sync time, record counts, and allows triggering new syncs
 */

import React, { useState } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Database,
  Play,
  Loader2,
  ChevronDown,
  ChevronUp,
  Box,
  Users,
  Package,
  Layers,
  ClipboardList,
  Settings
} from 'lucide-react';
import clsx from 'clsx';

// Sync Log entry from the API
export interface SyncLogEntry {
  id: string;
  entity_type: string;
  status: 'running' | 'success' | 'partial' | 'failed' | 'cancelled';
  started_at: string;
  completed_at: string | null;
  records_fetched: number;
  records_created: number;
  records_updated: number;
  records_skipped: number;
  records_failed: number;
  pages_fetched: number;
  duration_seconds: number | null;
  error_message: string | null;
  is_delta_sync: boolean;
}

export interface SyncStatusWidgetProps {
  data?: SyncLogEntry[];
  loading?: boolean;
  onTriggerSync?: (entityType?: string) => Promise<void>;
  onRefresh?: () => void;
}

// Entity type to icon mapping
const entityIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'work_center': Settings,
  'employee': Users,
  'item': Package,
  'bom_line': Layers,
  'production_order': ClipboardList,
  'operation': Box,
};

// Entity type to display name mapping
const entityNames: Record<string, string> = {
  'work_center': 'Work Centers',
  'employee': 'Employees',
  'item': 'Items',
  'bom_line': 'BOM Lines',
  'production_order': 'Production Orders',
  'operation': 'Operations',
};

// Status color mapping
const statusColors: Record<string, { bg: string; text: string; icon: React.ComponentType<{ className?: string }> }> = {
  'success': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  'partial': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle },
  'failed': { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  'running': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Loader2 },
  'cancelled': { bg: 'bg-gray-100', text: 'text-gray-700', icon: XCircle },
};

export function SyncStatusWidget({
  data = [],
  loading,
  onTriggerSync,
  onRefresh,
}: SyncStatusWidgetProps) {
  const [expandedEntity, setExpandedEntity] = useState<string | null>(null);
  const [syncingEntity, setSyncingEntity] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);

  // Handle sync trigger for a single entity
  const handleSyncEntity = async (entityType: string) => {
    if (!onTriggerSync || syncingEntity || syncingAll) return;
    setSyncingEntity(entityType);
    try {
      await onTriggerSync(entityType);
    } finally {
      setSyncingEntity(null);
    }
  };

  // Handle sync all
  const handleSyncAll = async () => {
    if (!onTriggerSync || syncingEntity || syncingAll) return;
    setSyncingAll(true);
    try {
      await onTriggerSync();
    } finally {
      setSyncingAll(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB');
  };

  // Format duration
  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Format number with thousands separator
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-GB');
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-3">
          <div className="flex justify-between items-center mb-4">
            <div className="h-6 w-32 bg-gray-200 rounded" />
            <div className="h-8 w-24 bg-gray-200 rounded" />
          </div>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  // Group data by entity type (latest entry per type)
  const latestByEntity = data.reduce((acc, entry) => {
    if (!acc[entry.entity_type] || new Date(entry.started_at) > new Date(acc[entry.entity_type].started_at)) {
      acc[entry.entity_type] = entry;
    }
    return acc;
  }, {} as Record<string, SyncLogEntry>);

  // Define entity order
  const entityOrder = ['work_center', 'employee', 'item', 'bom_line', 'production_order', 'operation'];
  const sortedEntities = entityOrder.filter(e => latestByEntity[e] || true);

  return (
    <div className="flex flex-col h-full">
      {/* Header with Sync All button */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-[#45bdd6]" />
          <span className="font-medium text-gray-700">Sync Status</span>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-2 rounded hover:bg-gray-200 text-gray-500 transition-colors"
              title="Refresh status"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          {onTriggerSync && (
            <button
              onClick={handleSyncAll}
              disabled={syncingAll || !!syncingEntity}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors',
                syncingAll
                  ? 'bg-blue-100 text-blue-700 cursor-wait'
                  : 'bg-[#45bdd6] text-white hover:bg-[#3ba8bf]',
                (syncingAll || syncingEntity) && 'opacity-75'
              )}
            >
              {syncingAll ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {syncingAll ? 'Syncing...' : 'Sync All'}
            </button>
          )}
        </div>
      </div>

      {/* Entity list */}
      <div className="flex-1 overflow-auto">
        {sortedEntities.map((entityType) => {
          const entry = latestByEntity[entityType];
          const Icon = entityIcons[entityType] || Box;
          const displayName = entityNames[entityType] || entityType;
          const isExpanded = expandedEntity === entityType;
          const isSyncing = syncingEntity === entityType;
          const statusConfig = entry ? statusColors[entry.status] : null;
          const StatusIcon = statusConfig?.icon || Clock;

          return (
            <div key={entityType} className="border-b border-gray-100">
              {/* Entity row */}
              <div
                className={clsx(
                  'flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors',
                  isExpanded && 'bg-gray-50'
                )}
                onClick={() => setExpandedEntity(isExpanded ? null : entityType)}
              >
                {/* Entity icon and name */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={clsx(
                    'p-2 rounded-lg',
                    entry ? statusConfig?.bg : 'bg-gray-100'
                  )}>
                    <Icon className={clsx('w-4 h-4', entry ? statusConfig?.text : 'text-gray-400')} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{displayName}</div>
                    <div className="text-xs text-gray-500">
                      {entry ? formatRelativeTime(entry.completed_at || entry.started_at) : 'Never synced'}
                    </div>
                  </div>
                </div>

                {/* Status badge */}
                {entry && (
                  <div className={clsx(
                    'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                    statusConfig?.bg,
                    statusConfig?.text
                  )}>
                    <StatusIcon className={clsx('w-3 h-3', entry.status === 'running' && 'animate-spin')} />
                    <span className="capitalize">{entry.status}</span>
                  </div>
                )}

                {/* Record count */}
                {entry && entry.status !== 'running' && (
                  <div className="ml-3 text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {formatNumber(entry.records_fetched)}
                    </div>
                    <div className="text-xs text-gray-500">records</div>
                  </div>
                )}

                {/* Sync button for single entity */}
                {onTriggerSync && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSyncEntity(entityType);
                    }}
                    disabled={isSyncing || syncingAll}
                    className={clsx(
                      'ml-3 p-2 rounded transition-colors',
                      isSyncing
                        ? 'bg-blue-100 text-blue-600'
                        : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600',
                      (isSyncing || syncingAll) && 'cursor-wait'
                    )}
                    title={`Sync ${displayName}`}
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Expand/collapse icon */}
                <div className="ml-2 text-gray-400">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && entry && (
                <div className="px-4 pb-4 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-white rounded-lg border border-gray-200">
                    <DetailItem
                      label="Created"
                      value={formatNumber(entry.records_created)}
                      color="text-green-600"
                    />
                    <DetailItem
                      label="Updated"
                      value={formatNumber(entry.records_updated)}
                      color="text-blue-600"
                    />
                    <DetailItem
                      label="Skipped"
                      value={formatNumber(entry.records_skipped)}
                      color="text-gray-600"
                    />
                    <DetailItem
                      label="Failed"
                      value={formatNumber(entry.records_failed)}
                      color={entry.records_failed > 0 ? 'text-red-600' : 'text-gray-600'}
                    />
                    <DetailItem
                      label="Duration"
                      value={formatDuration(entry.duration_seconds)}
                    />
                    <DetailItem
                      label="Pages"
                      value={String(entry.pages_fetched)}
                    />
                    <DetailItem
                      label="Type"
                      value={entry.is_delta_sync ? 'Delta' : 'Full'}
                    />
                    <DetailItem
                      label="Started"
                      value={new Date(entry.started_at).toLocaleString('en-GB')}
                    />
                  </div>

                  {/* Error message if failed */}
                  {entry.error_message && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-red-700">{entry.error_message}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {Object.keys(latestByEntity).length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Database className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-sm">No sync history available</p>
            {onTriggerSync && (
              <button
                onClick={handleSyncAll}
                className="mt-4 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3ba8bf] transition-colors"
              >
                Start Initial Sync
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Detail item component for expanded view
function DetailItem({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={clsx('text-sm font-medium', color || 'text-gray-900')}>{value}</div>
    </div>
  );
}
